import type {SiteLocale} from "seo/siteMeta";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";
import {hostedUploadLimitLabel} from "pricing/planEntitlements";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_DE, INSTANT_ONE_OFF_PER_TRANSFER as INSTANT_ONE_OFF_PER_TRANSFER_DE} from "i18n/de/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_EN, INSTANT_ONE_OFF_PER_TRANSFER as INSTANT_ONE_OFF_PER_TRANSFER_EN} from "i18n/en/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_RO, INSTANT_ONE_OFF_PER_TRANSFER as INSTANT_ONE_OFF_PER_TRANSFER_RO} from "i18n/ro/planLabels";

const PLUS_UPLOAD_EN = hostedUploadLimitLabel("plus", "unlimited", "en");
const PREMIUM_UPLOAD_EN = hostedUploadLimitLabel("premium", "unlimited", "en");
const PLUS_UPLOAD_RO = hostedUploadLimitLabel("plus", "nelimitat", "ro");
const PREMIUM_UPLOAD_RO = hostedUploadLimitLabel("premium", "fără limită", "ro");
const PREMIUM_UPLOAD_SIZE_RO = hostedUploadLimitLabel("premium", "Nelimitată", "ro");
// German needs no second "unlimited" form beside these: "unbegrenzt" is invariant where Romanian has
// to choose between a masculine, a feminine and a plural.
const PLUS_UPLOAD_DE = hostedUploadLimitLabel("plus", "unbegrenzt", "de");
const PREMIUM_UPLOAD_DE = hostedUploadLimitLabel("premium", "unbegrenzt", "de");

/**
 * Per-page copy for the public **Client-Server (hosted / relay) transfer mode** page
 * (`/client-server`, issue ui#133).
 *
 * One of the TWO files that own this page (the other is {@link ../ClientServerContent.tsx}). Holds
 * every locale; the page selects one at runtime via `useLocale()`, so the `/ro` and `/de` SSG trees
 * render their own language for free. Routes, SEO wiring and the Resources link already exist - only
 * the strings below and the content component's markup carry the page.
 *
 * The shape below is intentionally richer than the bare `body: string[]` scaffold: the hosted mode is
 * best explained as a "how it works" walkthrough plus a side-by-side comparison with peer-to-peer, so
 * the copy is structured into named sections the dumb view renders as semantic HTML.
 */
export interface ClientServerCopy {
	/** `<Seo>` metadata - the browser/search title and meta description for this page. */
	seo: {
		/** Page title (the site name is appended automatically by `<Seo>`). */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/** The single `<h1>` at the top of the page. */
	heading: string;
	/** One-line intro paragraph directly under the heading. */
	lede: string;
	/** Framing paragraphs under the lede - each renders as its own `<p>`. */
	intro: string[];
	/** "How a hosted transfer works" - an ordered walkthrough of the upload → link → download path. */
	how: {
		heading: string;
		/** Ordered steps; each renders as an `<li>` in an `<ol>`. */
		steps: string[];
	};
	/** Reliability highlights - an intro line plus a bullet list of titled points. */
	reliability: {
		heading: string;
		intro: string;
		items: {title: string; body: string}[];
	};
	/** Encryption section. `zk` is a sentence split around an inline link to `/zero-knowledge`. */
	encryption: {
		heading: string;
		body: string;
		zk: {before: string; link: string; after: string};
	};
	/** Side-by-side comparison table (client-server vs peer-to-peer). */
	compare: {
		heading: string;
		intro: string;
		/** Header for the leading row-label column. */
		featureLabel: string;
		/** Column headers for the two modes. */
		hostedLabel: string;
		p2pLabel: string;
		/** One row per compared aspect. */
		rows: {label: string; hosted: string; p2p: string}[];
	};
	/** "Which should you use?" closing help. `p2p` is a sentence split around a link to `/p2p`. */
	pick: {
		heading: string;
		hosted: string;
		p2p: {before: string; link: string; after: string};
		/**
		 * Links out to the industry landing pages this locale's content corpus ships, or `null`.
		 *
		 * Nullable rather than optional so every locale has to state its answer: the slugs are
		 * localized, and a locale inheriting English copy here would link addresses its own tree does
		 * not serve. English ships the two pages added by kitecorp/transferify-ui#2787, which is also
		 * what gives them a third inbound page for the content-reach rule; Romanian and German address
		 * their much larger industry corpus from their own pillar page instead.
		 */
		industries: {
			/** The sentence up to the first link. */
			lead: string;
			/** One entry per page: the anchor, and the text continuing the sentence after it. */
			links: {to: string; label: string; tail: string}[];
		} | null;
	};
}

/** EN + RO + DE copy, keyed by locale. `ClientServerContent` reads `clientServerCopy[locale]`. */
export const clientServerCopy: Record<SiteLocale, ClientServerCopy> = localeCopyWithEnglishFallback<ClientServerCopy>({
	en: {
		seo: {
			title: "Hosted (client-server) transfer, encrypted",
			description:
				"Transferify's hosted mode encrypts files on your device and stores them, so the recipient " +
				"can download later - even after you close the tab.",
		},
		heading: "Client-server transfer",
		lede:
			"Upload once, share a link, and let the recipient download whenever they're ready - even after " +
			"you close the tab. Every file is encrypted on your device before it leaves it.",
		intro: [
			"In client-server mode - the classic way most transfer tools work - your files are uploaded to " +
				"Transferify's storage first, then handed to the recipient through a download link. Because the " +
				"file lives on our servers, the transfer is asynchronous: you and the recipient never have to be " +
				"online at the same time.",
			"That's the key difference from a live peer-to-peer transfer. Hosted links cost you an upload wait " +
				"up front, but once it's done the link keeps working on its own - the recipient can grab the file " +
				"minutes or days later, from any device, long after your tab is closed.",
		],
		how: {
			heading: "How a hosted transfer works",
			steps: [
				"Your files are encrypted on your device with AES-256-GCM before a single byte leaves it - the " +
					"server only ever sees ciphertext.",
				"The encrypted data is split into chunks and sent straight to storage as a multipart upload, so a " +
					"large file goes up as many independent parts instead of one fragile stream. The chunk size is " +
					"chosen per file - 8 MiB for everyday transfers, scaling up as the file grows - so even the " +
					"largest upload still fits the 10,000-part limit.",
				"Any part that fails is retried automatically with exponential backoff, and its upload URL is " +
					"re-signed on every attempt - so a brief network drop doesn't cost you the whole upload.",
				"Once the upload finishes, your share link goes live. The recipient opens it and the file streams " +
					"down chunk by chunk, decrypted on the fly in their browser - no account, nothing to install.",
			],
		},
		reliability: {
			heading: "Built for big files and shaky networks",
			intro: "Holding the file server-side is what makes this mode dependable:",
			items: [
				{
					title: "Chunked, multipart upload",
					body:
						"A large file is sliced into parts and uploaded independently, so throughput stays high and a " +
						"single dropped part never restarts the whole transfer. Part size grows with the file, so even " +
						"the biggest upload we accept still fits inside the same 10,000-part ceiling.",
				},
				{
					title: "Automatic retries",
					body:
						"Each part gets up to three attempts with exponential backoff and a freshly signed URL, so a " +
						"multi-hour upload over unreliable Wi-Fi still finishes.",
				},
				{
					title: "Large files",
					body:
						`Start free, then send up to ${PLUS_UPLOAD_EN} on Plus and ${PREMIUM_UPLOAD_EN} on Premium - the multipart engine carries ` +
						"the size.",
				},
				{
					title: "Streamed, memory-safe downloads",
					body:
						"The recipient's browser pulls the file one chunk at a time and writes it straight to disk, so " +
						"even huge files never have to fit in memory - and the download can be re-run any time before " +
						"the link expires.",
				},
			],
		},
		encryption: {
			heading: "Still end-to-end encrypted",
			body:
				"Hosted doesn't mean exposed. Your files are encrypted on your device with AES-256-GCM before " +
				"they're uploaded and stay encrypted at rest - Transferify's servers only ever handle ciphertext, " +
				"never your plaintext. Every chunk is sealed separately and bound to its position in the file, so " +
				"the download fails loudly if a single byte is tampered with or reordered.",
			zk: {
				before:
					"By default we hold the key, wrapped by a cloud managed encryption key, so a lost link is " +
					"still recoverable; switch on Zero-Knowledge mode and the key never reaches us at all. ",
				link: "See how zero-knowledge encryption works",
				after: ".",
			},
		},
		compare: {
			heading: "Client-server vs peer-to-peer",
			intro:
				"Two ways to send the same file. Client-server stores it so it can wait; peer-to-peer streams it " +
				"live, device to device, and stores nothing.",
			featureLabel: "Aspect",
			hostedLabel: "Client-server",
			p2pLabel: "Peer-to-peer",
			rows: [
				{label: "When the link works", hosted: "As soon as the upload finishes", p2p: "Instantly, before any upload"},
				{label: "Path the file takes", hosted: "Device → server → device", p2p: "Device → device, directly"},
				{label: "Recipient", hosted: "Downloads anytime, even days later", p2p: "Must be online while you send"},
				{label: "When you close your tab", hosted: "Link stays online", p2p: "Transfer ends"},
				{label: "Where the file lives", hosted: "Encrypted on our servers until it expires", p2p: "Nowhere - never stored"},
				{label: "File size", hosted: `Free to start · ${PLUS_UPLOAD_EN} Plus · ${PREMIUM_UPLOAD_EN} Premium`, p2p: `Free up to ${INSTANT_FREE_SIZE_EN} · any size for a one-off ${INSTANT_ONE_OFF_PER_TRANSFER_EN}`},
			],
		},
		pick: {
			heading: "Which should you use?",
			hosted:
				"Choose client-server when the recipient may be offline, when you want the link to keep working " +
				"after you close your tab, or when you need passwords, expiry dates and download limits on the file.",
			p2p: {
				before: "Choose ",
				link: "peer-to-peer",
				after:
					" when you're both online and want the file to stream instantly, at full speed, with nothing " +
					"ever stored on a server.",
			},
			industries: {
				lead: "Sending a set of drawings from a site office, or a season catalogue to a printer? The page for ",
				links: [
					{
						to: "/for/construction-and-trades",
						label: "construction and trades",
						tail: " walks through drawings, quotes and site photos, and the page for ",
					},
					{
						to: "/for/retail-and-manufacturing",
						label: "retail and manufacturing",
						tail: " does the same for catalogues, product photos and print-ready files.",
					},
				],
			},
		},
	},
	ro: {
		seo: {
			title: "Transfer găzduit client-server, criptat",
			description:
				"Modul găzduit criptează fișierele pe dispozitivul tău și le stochează, așa că destinatarul " +
				"le poate descărca și după ce închizi fila.",
		},
		heading: "Transfer client-server",
		lede:
			"Încarci o singură dată, partajezi un link, iar destinatarul descarcă atunci când e pregătit - " +
			"chiar și după ce închizi fila. Fiecare fișier este criptat pe dispozitivul tău înainte să îl părăsească.",
		intro: [
			"În modul client-server - varianta clasică, așa cum funcționează majoritatea instrumentelor de " +
				"transfer - fișierele tale sunt încărcate mai întâi în stocarea Transferify, apoi predate " +
				"destinatarului printr-un link de descărcare. Fiindcă fișierul stă pe serverele noastre, transferul " +
				"este asincron: tu și destinatarul nu trebuie să fiți online în același timp.",
			"Aceasta este diferența esențială față de un transfer peer-to-peer live. Linkurile găzduite îți cer " +
				"o așteptare la încărcare la început, dar odată terminată, linkul funcționează singur - destinatarul " +
				"poate lua fișierul peste câteva minute sau peste câteva zile, de pe orice dispozitiv, mult după ce " +
				"fila ta s-a închis.",
		],
		how: {
			heading: "Cum funcționează un transfer găzduit",
			steps: [
				"Fișierele tale sunt criptate pe dispozitiv cu AES-256-GCM înainte ca vreun octet să îl părăsească " +
					"- serverul vede doar text cifrat.",
				"Datele criptate sunt împărțite în bucăți și trimise direct în stocare printr-o încărcare multipart, " +
					"așa că un fișier mare urcă în multe segmente independente, nu într-un singur flux fragil. " +
					"Dimensiunea bucății este aleasă pentru fiecare fișier - 8 MiB pentru transferurile obișnuite și " +
					"mai mare pe măsură ce fișierul crește - astfel încât și cea mai mare încărcare încape în limita " +
					"de 10.000 de segmente.",
				"Orice segment care eșuează este reîncercat automat, cu pauze care se dublează, iar URL-ul său de " +
					"încărcare este re-semnat la fiecare încercare - așa că o scurtă întrerupere de rețea nu te costă " +
					"toată încărcarea.",
				"Când încărcarea se termină, linkul de partajare devine activ. Destinatarul îl deschide, iar fișierul " +
					"se descarcă bucată cu bucată, decriptate din mers în browserul lui - fără cont, fără nimic de instalat.",
			],
		},
		reliability: {
			heading: "Făcut pentru fișiere mari și conexiuni instabile",
			intro: "Păstrarea fișierului pe server este ceea ce face acest mod fiabil:",
			items: [
				{
					title: "Încărcare multipart, în bucăți",
					body:
						"Un fișier mare este împărțit în segmente și încărcat independent, așa că viteza rămâne ridicată, " +
						"iar un singur segment pierdut nu repornește tot transferul. Dimensiunea segmentului crește " +
						"odată cu fișierul, așa că și cea mai mare încărcare pe care o acceptăm încape în aceeași " +
						"limită de 10.000 de segmente.",
				},
				{
					title: "Reîncercări automate",
					body:
						"Fiecare segment primește până la trei încercări, cu pauze care se dublează și un URL proaspăt " +
						"semnat, așa că o încărcare de câteva ore, pe un Wi-Fi nesigur, tot se finalizează.",
				},
				{
					title: "Fișiere mari",
					body:
						`Începi gratuit, apoi trimiți până la ${PLUS_UPLOAD_RO} pe Plus și ${PREMIUM_UPLOAD_RO} pe Premium - motorul multipart duce ` +
						"dimensiunea.",
				},
				{
					title: "Descărcări în flux, sigure pentru memorie",
					body:
						"Browserul destinatarului aduce fișierul bucată cu bucată și îl scrie direct pe disc, așa că " +
						"nici fișierele uriașe nu trebuie să încapă în memorie - iar descărcarea poate fi reluată oricând " +
						"înainte de expirarea linkului.",
				},
			],
		},
		encryption: {
			heading: "Tot criptat end-to-end",
			body:
				"Găzduit nu înseamnă expus. Fișierele tale sunt criptate pe dispozitiv cu AES-256-GCM înainte de a " +
				"fi încărcate și rămân criptate în repaus - serverele Transferify manipulează doar text cifrat, " +
				"niciodată conținutul tău în clar. Fiecare bucată este sigilată separat și legată de poziția ei în " +
				"fișier, așa că descărcarea eșuează zgomotos dacă un singur octet este alterat sau reordonat.",
			zk: {
				before:
					"În mod implicit păstrăm cheia fișierului, împachetată cu o cheie de criptare gestionată în " +
					"cloud, ca un link pierdut să rămână recuperabil; activează modul Zero-Knowledge și cheia " +
					"nu ajunge deloc la noi. ",
				link: "Vezi cum funcționează criptarea zero-knowledge",
				after: ".",
			},
		},
		compare: {
			heading: "Client-server vs. peer-to-peer",
			intro:
				"Două moduri de a trimite același fișier. Client-server îl stochează ca să poată aștepta; " +
				"peer-to-peer îl transmite live, de la un dispozitiv la altul, și nu stochează nimic.",
			featureLabel: "Aspect",
			hostedLabel: "Client-server",
			p2pLabel: "Peer-to-peer",
			rows: [
				{label: "Când funcționează linkul", hosted: "Imediat ce se termină încărcarea", p2p: "Instant, înainte de orice încărcare"},
				{label: "Traseul fișierului", hosted: "Dispozitiv → server → dispozitiv", p2p: "Dispozitiv → dispozitiv, direct"},
				{label: "Destinatar", hosted: "Descarcă oricând, chiar și peste zile", p2p: "Trebuie să fie online în timp ce trimiți"},
				{label: "Când închizi fila", hosted: "Linkul rămâne activ", p2p: "Transferul se oprește"},
				{label: "Unde stă fișierul", hosted: "Criptat pe serverele noastre până expiră", p2p: "Nicăieri - niciodată stocat"},
				{label: "Dimensiunea fișierului", hosted: `Gratuit la start · ${PLUS_UPLOAD_RO} Plus · ${PREMIUM_UPLOAD_SIZE_RO} Premium`, p2p: `Gratuit până la ${INSTANT_FREE_SIZE_RO} · orice dimensiune cu o plată unică de ${INSTANT_ONE_OFF_PER_TRANSFER_RO}`},
			],
		},
		pick: {
			heading: "Pe care să îl folosești?",
			hosted:
				"Alege client-server când destinatarul ar putea fi offline, când vrei ca linkul să funcționeze și " +
				"după ce închizi fila sau când ai nevoie de parole, date de expirare și limite de descărcare pentru fișier.",
			p2p: {
				before: "Alege ",
				link: "peer-to-peer",
				after:
					" când sunteți amândoi online și vrei ca fișierul să circule instant, la viteză maximă, fără ca " +
					"nimic să fie stocat vreodată pe server.",
			},
			industries: null,
		},
	},
	de: {
		seo: {
			// 60 characters INCLUDING the " | Transferify" that `<Seo>` appends, which the SSG head guard
			// enforces at build time: "Gehosteter Client-Server-Transfer, verschlüsselt" came to 62.
			title: "Client-Server-Transfer, verschlüsselt",
			description:
				"Der gehostete Modus verschlüsselt Dateien auf deinem Gerät und speichert sie, sodass der " +
				"Empfänger auch nach dem Schließen deines Tabs herunterladen kann.",
		},
		heading: "Client-Server-Transfer",
		lede:
			"Einmal hochladen, einen Link teilen, und der Empfänger lädt herunter, wann er so weit ist - auch " +
			"nachdem du den Tab geschlossen hast. Jede Datei wird auf deinem Gerät verschlüsselt, bevor sie es verlässt.",
		intro: [
			"Im Client-Server-Modus - dem klassischen Weg, so wie die meisten Transfer-Werkzeuge arbeiten - " +
				"werden deine Dateien zuerst in den Speicher von Transferify hochgeladen und dem Empfänger dann " +
				"über einen Download-Link übergeben. Weil die Datei auf unseren Servern liegt, läuft der Transfer " +
				"asynchron: Du und der Empfänger müsst nie gleichzeitig online sein.",
			"Das ist der entscheidende Unterschied zu einem Peer-to-Peer-Transfer in Echtzeit. Gehostete Links " +
				"kosten dich vorab eine Wartezeit beim Hochladen, aber danach funktioniert der Link von allein - " +
				"der Empfänger holt die Datei Minuten oder Tage später, von jedem Gerät, lange nachdem dein Tab " +
				"zu ist.",
		],
		how: {
			heading: "So läuft ein gehosteter Transfer ab",
			steps: [
				"Deine Dateien werden auf deinem Gerät mit AES-256-GCM verschlüsselt, bevor ein einziges Byte es " +
					"verlässt - der Server sieht immer nur Geheimtext.",
				"Die verschlüsselten Daten werden in Stücke geteilt und als Multipart-Upload direkt in den Speicher " +
					"geschickt, sodass eine große Datei in vielen unabhängigen Teilen hochgeht statt in einem " +
					"anfälligen Strom. Die Stückgröße wird pro Datei gewählt - 8 MiB für alltägliche Transfers, " +
					"größer, je größer die Datei wird - damit auch der größte Upload noch in die Grenze von 10.000 " +
					"Teilen passt.",
				"Jedes Teil, das fehlschlägt, wird automatisch wiederholt, mit exponentiell wachsenden Pausen, und " +
					"seine Upload-URL wird bei jedem Versuch neu signiert - eine kurze Netzstörung kostet dich also " +
					"nicht den ganzen Upload.",
				"Sobald der Upload fertig ist, geht dein Freigabelink live. Der Empfänger öffnet ihn, und die Datei " +
					"läuft Stück für Stück herunter und wird im Browser direkt entschlüsselt - ohne Konto, ohne " +
					"etwas zu installieren.",
			],
		},
		reliability: {
			heading: "Gebaut für große Dateien und wackelige Netze",
			intro: "Dass die Datei auf dem Server liegt, macht diesen Modus verlässlich:",
			items: [
				{
					title: "Multipart-Upload in Stücken",
					body:
						"Eine große Datei wird in Teile geschnitten und unabhängig hochgeladen, sodass der Durchsatz hoch " +
						"bleibt und ein einzelnes verlorenes Teil nie den ganzen Transfer neu startet. Die Teilgröße " +
						"wächst mit der Datei, damit auch der größte Upload, den wir annehmen, in dieselbe Grenze von " +
						"10.000 Teilen passt.",
				},
				{
					title: "Automatische Wiederholungen",
					body:
						"Jedes Teil bekommt bis zu drei Versuche, mit exponentiell wachsenden Pausen und einer frisch " +
						"signierten URL, sodass auch ein mehrstündiger Upload über unzuverlässiges WLAN fertig wird.",
				},
				{
					title: "Große Dateien",
					body:
						`Fang kostenlos an, sende dann bis zu ${PLUS_UPLOAD_DE} auf Plus und ${PREMIUM_UPLOAD_DE} auf Premium - die Multipart-Maschinerie ` +
						"trägt die Größe.",
				},
				{
					title: "Gestreamte, speicherschonende Downloads",
					body:
						"Der Browser des Empfängers holt die Datei Stück für Stück und schreibt sie direkt auf die " +
						"Platte, sodass auch riesige Dateien nie in den Arbeitsspeicher passen müssen - und der Download " +
						"lässt sich jederzeit wiederholen, solange der Link nicht abgelaufen ist.",
				},
			],
		},
		encryption: {
			heading: "Trotzdem Ende-zu-Ende-verschlüsselt",
			body:
				"Gehostet heißt nicht offen. Deine Dateien werden auf deinem Gerät mit AES-256-GCM verschlüsselt, " +
				"bevor sie hochgeladen werden, und bleiben im Speicher verschlüsselt - die Server von Transferify " +
				"bekommen immer nur Geheimtext zu fassen, nie deinen Klartext. Jedes Stück wird einzeln versiegelt " +
				"und an seine Position in der Datei gebunden, sodass der Download laut scheitert, wenn ein einziges " +
				"Byte verändert oder umgestellt wurde.",
			zk: {
				before:
					"Standardmäßig hinterlegen wir den Schlüssel, eingepackt von einem cloud-verwalteten " +
					"Verschlüsselungsschlüssel, damit ein verlorener Link wiederherstellbar bleibt; schalte den " +
					"Zero-Knowledge-Modus ein, und der Schlüssel erreicht uns gar nicht erst. ",
				link: "Sieh dir an, wie Zero-Knowledge-Verschlüsselung funktioniert",
				after: ".",
			},
		},
		compare: {
			heading: "Client-Server gegen Peer-to-Peer",
			intro:
				"Zwei Wege, dieselbe Datei zu senden. Client-Server speichert sie, damit sie warten kann; " +
				"Peer-to-Peer überträgt sie live, von Gerät zu Gerät, und speichert nichts.",
			featureLabel: "Aspekt",
			hostedLabel: "Client-Server",
			p2pLabel: "Peer-to-Peer",
			rows: [
				{label: "Ab wann der Link geht", hosted: "Sobald der Upload fertig ist", p2p: "Sofort, vor jedem Upload"},
				{label: "Weg der Datei", hosted: "Gerät → Server → Gerät", p2p: "Gerät → Gerät, direkt"},
				{label: "Empfänger", hosted: "Lädt jederzeit herunter, auch Tage später", p2p: "Muss online sein, während du sendest"},
				{label: "Wenn du deinen Tab schließt", hosted: "Link bleibt online", p2p: "Transfer endet"},
				{label: "Wo die Datei liegt", hosted: "Verschlüsselt auf unseren Servern bis zum Ablauf", p2p: "Nirgends - nie gespeichert"},
				{label: "Dateigröße", hosted: `Kostenlos starten · ${PLUS_UPLOAD_DE} Plus · ${PREMIUM_UPLOAD_DE} Premium`, p2p: `Bis ${INSTANT_FREE_SIZE_DE} kostenlos · jede Größe per Einmalzahlung von ${INSTANT_ONE_OFF_PER_TRANSFER_DE}`},
			],
		},
		pick: {
			heading: "Welchen solltest du nehmen?",
			hosted:
				"Nimm Client-Server, wenn der Empfänger offline sein könnte, wenn der Link auch nach dem Schließen " +
				"deines Tabs weiter funktionieren soll, oder wenn du Passwörter, Ablaufdaten und Download-Limits " +
				"für die Datei brauchst.",
			p2p: {
				before: "Nimm ",
				link: "Peer-to-Peer",
				after:
					", wenn ihr beide online seid und die Datei sofort und mit voller Geschwindigkeit laufen soll, " +
					"ohne dass je etwas auf einem Server gespeichert wird.",
			},
			industries: null,
		},
	},
});
